import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import PageHero from "@/components/PageHero";
import { Download, FileText, X, CheckCircle2 } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1457369804613-52c61a468e7d?auto=format&fit=crop&w=1400&q=80";

const ACCENTS = {
  salary_guide: "var(--accent-purple)",
  talent_assessment: "var(--accent-teal)",
  vendor_audit: "var(--accent-amber)",
};
const TAGS = {
  salary_guide: "Guide · 2025 Edition",
  talent_assessment: "Diagnostic · 15 Questions",
  vendor_audit: "Checklist · 32 Criteria",
};

export default function Resources() {
  const [magnets, setMagnets] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/lead-magnets")
      .then((r) => setMagnets(r.data.magnets))
      .catch(() => toast.error("Could not load resources."));
  }, []);

  const openDialog = (type) => { setActive(type); setForm({ name: "", email: "", company: "" }); };
  const close = () => setActive(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!active) return;
    setSubmitting(true);
    try {
      const res = await api.post("/lead-magnets/register", {
        email: form.email,
        name: form.name || null,
        company: form.company || null,
        lead_magnet_type: active,
      });
      const downloadUrl = `${window.location.origin}${res.data.download_url}`;
      // Trigger download
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = "";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(`${res.data.title} — download started. Check your Downloads folder.`);
      close();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="resources-page">
      <PageHero
        eyebrow="Insights & Resources"
        title={<>Practical tools, <span className="italic" style={{ color: "var(--accent-purple)" }}>not thought-leadership fluff.</span></>}
        subtitle="Three free, gated downloads used by Canadian CTOs, COOs, and procurement leaders. Submit your email — we deliver the PDF instantly."
        image={HERO_IMG}
        imageAlt="Canadian insights resources"
        testId="resources-hero"
      />

      <section className="py-16 md:py-24" data-testid="resources-list">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 grid md:grid-cols-3 gap-6">
          {magnets.map((m) => (
            <div key={m.type} className="practice-card flex flex-col h-full"
                 style={{ "--accent": ACCENTS[m.type] }}
                 data-testid={`resource-card-${m.type}`}>
              <div className="eyebrow mb-4" style={{ color: ACCENTS[m.type] }}>{TAGS[m.type]}</div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-5"
                   style={{ background: `${ACCENTS[m.type]}22`, color: ACCENTS[m.type] }}>
                <FileText size={22} />
              </div>
              <h3 className="font-display text-xl md:text-2xl leading-tight mb-3">{m.title}</h3>
              <p className="text-sm text-[color:var(--muted)] leading-relaxed flex-1">{m.description}</p>
              <button
                type="button"
                onClick={() => openDialog(m.type)}
                className="btn-primary mt-6 self-start"
                data-testid={`resource-download-btn-${m.type}`}
              >
                Download <Download size={16} />
              </button>
            </div>
          ))}
        </div>

        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 mt-14 text-sm text-[color:var(--muted)]">
          PIPEDA-compliant · No newsletter sign-up · We use your email to follow up once.
        </div>
      </section>

      {active && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[color:var(--primary-dark)]/60 backdrop-blur-sm" role="dialog" data-testid="lead-magnet-dialog">
          <form
            onSubmit={submit}
            className="bg-white rounded-2xl max-w-md w-full p-8 relative shadow-2xl"
            data-testid="lead-magnet-form"
          >
            <button type="button" onClick={close} className="absolute top-4 right-4 text-[color:var(--muted)] hover:text-[color:var(--primary-dark)]" aria-label="Close" data-testid="lead-magnet-close-btn">
              <X size={20} />
            </button>
            <div className="eyebrow mb-2" style={{ color: ACCENTS[active] }}>{TAGS[active]}</div>
            <h3 className="font-display text-2xl mb-2">Get instant access</h3>
            <p className="text-sm text-[color:var(--muted)] mb-6">Delivered to your device as a PDF. No drip sequence.</p>
            <div className="space-y-4">
              <div>
                <label className="field-label">Full name</label>
                <input className="field-input" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} data-testid="lead-magnet-field-name" />
              </div>
              <div>
                <label className="field-label">Work email *</label>
                <input required type="email" className="field-input" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} data-testid="lead-magnet-field-email" />
              </div>
              <div>
                <label className="field-label">Company</label>
                <input className="field-input" value={form.company} onChange={(e) => setForm((s) => ({ ...s, company: e.target.value }))} data-testid="lead-magnet-field-company" />
              </div>
            </div>
            <button type="submit" disabled={submitting} className="btn-primary w-full mt-6" data-testid="lead-magnet-submit-btn">
              {submitting ? "Preparing…" : (<>Download PDF <Download size={16} /></>)}
            </button>
            <div className="flex items-start gap-2 mt-4 text-xs text-[color:var(--muted)]">
              <CheckCircle2 size={14} className="shrink-0 mt-0.5" style={{ color: "var(--accent-mint)" }} />
              Your email is stored securely. PIPEDA-compliant.
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
